import cx from 'classix';
import { Key, LegacyRef, ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import type { MenuItemProps } from 'react-aria-components';
import { Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import { Icon, Icons } from '../atoms';
import { Button, IconButton } from './Button';

type Props = (
  | {
      type: 'button';
      buttonProps: Parameters<typeof Button>[0];
    }
  | {
      type: 'icon';
      iconName: keyof typeof Icons;
      iconSize?: 'sm' | 'md' | 'lg';
    }
  | {
      type: 'avatar';
      avatarUrl: string;
    }
) & {
  children: ReactNode;
  ariaLabel: string;
  onAction?: ((key: Key) => void) | undefined;
  href?: string;
};

export const DropdownItem = forwardRef(
  (
    props: {
      isActive?: boolean;
      additionalClassnames?: string;
    } & MenuItemProps,
    ref,
  ) => {
    const { additionalClassnames = '' } = props;

    return (
      // Note the 'block'; setting the href property on a MenuItem makes it render as
      // an <a> tag
      <MenuItem
        {...props}
        isDisabled={props.isActive}
        ref={ref as LegacyRef<HTMLDivElement>}
        className={cx(
          'block px-4 py-2 mx-1 my-0.5 rounded-md outline-none',
          props.isActive ? 'bg-gray-50 cursor-default' : 'hover:bg-gray-50 cursor-pointer',
          additionalClassnames,
        )}
      >
        {props.children}
      </MenuItem>
    );
  },
);

/** A menu-style dropdown of fixed items. Uses MenuItems https://react-spectrum.adobe.com/react-aria/Menu.html#menuitem as children
 * to support static markdown content & linking via hrefs. */
export const Dropdown = ({ children, ...props }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLElement>(null);

  /** React-aria-component Popovers try to behave like modalsy default for a11y reasons. This
   * means that clicking outside the popover will close it, but clicking on other interactive * elements when open will close it while not triggering that target. To step around this, we
   * set isNonModal on the Popover and then manually handle outside clicks here. */
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && e.target && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Skipping 'avatar' for now until the Avatar component is implemented
  if (props.type === 'avatar') {
    return <div>Not implemented</div>;
  }

  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.type === 'button' && (
        <Button aria-label='button' aria-haspopup='menu' {...props.buttonProps}>
          <span className={`max-w-full flex items-center font-sans [&>svg>path]:stroke-gray-500`}>
            {props.buttonProps.children}
            {/* Prevent the chevron becoming invisible when dropdown button content overflows */}
            <span className='w-[20px]'>
              {/* These empty onClicks are necessary to trigger the Icon's formatting to be used as a button, specifically
               * cursor-pointer */}
              {isOpen ?
                <Icon size='20' type='chevron-up' />
              : <Icon size='20' type='chevron-down' />}
            </span>
          </span>
        </Button>
      )}
      {props.type === 'icon' && (
        <IconButton
          aria-label='menu'
          icon={props.iconName}
          variant='tertiary-gray'
          size={props.iconSize ?? 'lg'}
          additionalClassnames='px-0 py-0 w-2'
        />
      )}
      <Popover
        ref={popoverRef}
        isNonModal
        className='text-sm max-h-[500px] overflow-y-auto shadow-md'
        placement='bottom end'
      >
        <Menu
          className={cx(
            'bg-white shadow-lg border border-gray-100 rounded-lg outline-none focused:outline',
            // Style react-aria's <Separator /> component
            '[&_.react-aria-Separator]:border-gray-100',
            '[&_.react-aria-Separator]:border-t-[1px]',
            '[&_.react-aria-Separator]:border-b-0',
            '[&_.react-aria-Separator]:border',
          )}
        >
          {children}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};
